import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  familyPlans: [],
  currentPlan: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all plans (optionally by patient ID)
export const fetchFamilyPlans = createAsyncThunk(
  "tentativeFamilyPlan/fetchAll",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/tentative-family-plans/${patientId}`
        : '/tentative-family-plans';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          "Failed to fetch tentative family plans"
      );
    }
  }
);

// Fetch a single plan
export const fetchFamilyPlan = createAsyncThunk(
  "tentativeFamilyPlan/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tentative-family-plans/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch tentative family plan"
      );
    }
  }
);

// Create plan
export const createFamilyPlan = createAsyncThunk(
  "tentativeFamilyPlan/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/tentative-family-plans', data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to create tentative family plan"
      );
    }
  }
);

// Update plan (✅ now uses case_id instead of id)
export const updateFamilyPlan = createAsyncThunk(
  "tentativeFamilyPlan/update",
  async ({ case_id, data }, { rejectWithValue }) => {
    try {
      // ✅ Updated endpoint to match Laravel route
      const response = await api.put(`/tentative-family-plans/case/${case_id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to update tentative family plan"
      );
    }
  }
);

// Delete plan
export const deleteFamilyPlan = createAsyncThunk(
  "tentativeFamilyPlan/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/tentative-family-plans/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete tentative family plan"
      );
    }
  }
);

// ==================== Slice ====================
const tentativeFamilyPlanSlice = createSlice({
  name: "tentativeFamilyPlan",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPlan: (state, action) => {
      state.currentPlan = action.payload;
    },
    clearCurrentPlan: (state) => {
      state.currentPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchFamilyPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFamilyPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.familyPlans = action.payload;
      })
      .addCase(fetchFamilyPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchFamilyPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFamilyPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPlan = action.payload;
      })
      .addCase(fetchFamilyPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createFamilyPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFamilyPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.familyPlans.unshift(action.payload);
      })
      .addCase(createFamilyPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateFamilyPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFamilyPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;
        const index = state.familyPlans.findIndex((p) => p.id === updated.id);
        if (index !== -1) {
          state.familyPlans[index] = updated;
        }
        if (state.currentPlan?.id === updated.id) {
          state.currentPlan = updated;
        }
      })
      .addCase(updateFamilyPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteFamilyPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFamilyPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.familyPlans = state.familyPlans.filter(
          (p) => p.id !== action.payload
        );
        if (state.currentPlan?.id === action.payload) {
          state.currentPlan = null;
        }
      })
      .addCase(deleteFamilyPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentPlan,
  clearCurrentPlan,
} = tentativeFamilyPlanSlice.actions;

export default tentativeFamilyPlanSlice.reducer;
